import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FolderKanban,
  Activity,
  Menu,
  X,
  Inbox,
  Package,
  LogOut,
  Bell,
  Camera,
  Key,
  ChevronDown,
  Sparkles,
} from 'lucide-react';
import { useUIStore } from '../../stores/uiStore';
import { useAuthStore } from '../../stores/authStore';
import { api } from '../../lib/api';
import { cn } from '../../lib/utils';
import { ThemeToggle } from '../ui/ThemeToggle';
import { UserSettingsModal } from '../modals/UserSettingsModal';

interface Project {
  id: string;
  name: string;
  iconUrl: string | null;
  coverImageUrl: string | null;
  pendingApprovalCount: number;
}

function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== 'undefined' ? window.innerWidth >= 1024 : true
  );

  useEffect(() => {
    const checkDesktop = () => setIsDesktop(window.innerWidth >= 1024);
    checkDesktop();
    window.addEventListener('resize', checkDesktop);
    return () => window.removeEventListener('resize', checkDesktop);
  }, []);

  return isDesktop;
}

export function Sidebar() {
  const location = useLocation();
  const { sidebarOpen, toggleSidebar, sidebarCollapsed } = useUIStore();
  const { user, logout } = useAuthStore();
  const isDesktop = useIsDesktop();
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [settingsTab, setSettingsTab] = useState<'profile' | 'password'>('profile');
  const userMenuRef = useRef<HTMLDivElement>(null);
  const notificationsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    async function fetchProjects() {
      try {
        const response = await api.get<Project[]>('/projects');
        setProjects(response.data);
      } catch (error) {
        console.error('Failed to fetch projects:', error);
      } finally {
        setIsLoading(false);
      }
    }
    fetchProjects();
  }, []);

  // Close sidebar on mobile when navigating
  useEffect(() => {
    if (!isDesktop && sidebarOpen) {
      toggleSidebar();
    }
  }, [location.pathname]);

  // Close menus when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setShowUserMenu(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(e.target as Node)) {
        setShowNotifications(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const openSettings = (tab: 'profile' | 'password') => {
    setSettingsTab(tab);
    setShowSettingsModal(true);
    setShowUserMenu(false);
  };

  const isHome = location.pathname === '/';

  return (
    <>
      {/* Mobile overlay */}
      <AnimatePresence>
        {sidebarOpen && !isDesktop && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={toggleSidebar}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
          />
        )}
      </AnimatePresence>

      {/* Mobile menu button */}
      <button
        onClick={toggleSidebar}
        className="fixed top-4 left-4 z-50 lg:hidden p-2 rounded-xl bg-bg-secondary border border-border shadow-lg"
      >
        {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
      </button>

      {/* Sidebar */}
      <motion.aside
        initial={false}
        animate={{
          x: isDesktop ? (sidebarCollapsed ? '-100%' : 0) : sidebarOpen ? 0 : '-100%',
        }}
        className={cn(
          'fixed top-0 left-0 h-full z-40 lg:z-30',
          'w-64 bg-bg-secondary/95 backdrop-blur-xl border-r border-border',
          'flex flex-col'
        )}
        transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      >
        {/* Logo */}
        <div className="h-16 flex items-center px-4 border-b border-border">
          <Link to="/" className="flex items-center">
            <img src="/OMLYodaLogo.png" alt="OML Yoda" className="h-8" />
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {/* All Activity */}
          <Link to="/" className="block">
            <motion.div
              whileHover={{ scale: 1.02, x: 4 }}
              whileTap={{ scale: 0.98 }}
              className={cn(
                'flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors relative',
                isHome
                  ? 'text-accent'
                  : 'text-text-secondary hover:text-text hover:bg-bg-tertiary'
              )}
            >
              {isHome && (
                <motion.div
                  layoutId="activeSidebar"
                  className="absolute inset-0 bg-accent/10 rounded-xl"
                  transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                />
              )}
              <Activity className="w-5 h-5 relative z-10" />
              <span className="font-medium relative z-10">All Activity</span>
            </motion.div>
          </Link>

          {/* Requests */}
          <Link to="/requests" className="block">
            <motion.div
              whileHover={{ scale: 1.02, x: 4 }}
              whileTap={{ scale: 0.98 }}
              className={cn(
                'flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors relative',
                location.pathname.startsWith('/requests')
                  ? 'text-accent'
                  : 'text-text-secondary hover:text-text hover:bg-bg-tertiary'
              )}
            >
              {location.pathname.startsWith('/requests') && (
                <motion.div
                  layoutId="activeSidebar"
                  className="absolute inset-0 bg-accent/10 rounded-xl"
                  transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                />
              )}
              <Inbox className="w-5 h-5 relative z-10" />
              <span className="font-medium relative z-10">My Requests</span>
            </motion.div>
          </Link>

          {/* Bundles */}
          <Link to="/bundles" className="block">
            <motion.div
              whileHover={{ scale: 1.02, x: 4 }}
              whileTap={{ scale: 0.98 }}
              className={cn(
                'flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors relative',
                location.pathname.startsWith('/bundles')
                  ? 'text-accent'
                  : 'text-text-secondary hover:text-text hover:bg-bg-tertiary'
              )}
            >
              {location.pathname.startsWith('/bundles') && (
                <motion.div
                  layoutId="activeSidebar"
                  className="absolute inset-0 bg-accent/10 rounded-xl"
                  transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                />
              )}
              <Package className="w-5 h-5 relative z-10" />
              <span className="font-medium relative z-10">Bundles</span>
            </motion.div>
          </Link>

          {/* Projects section */}
          <div className="pt-4">
            <div className="px-3 mb-2">
              <span className="text-xs font-semibold text-text-muted uppercase tracking-wider">
                Projects
              </span>
            </div>

            {isLoading ? (
              <div className="space-y-2 px-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-10 bg-bg-tertiary rounded-lg animate-pulse" />
                ))}
              </div>
            ) : projects.length === 0 ? (
              <div className="px-3 py-4 text-center text-text-muted text-sm">
                No projects yet
              </div>
            ) : (
              <div className="space-y-1">
                {projects.map((project) => {
                  const isActive = location.pathname === `/projects/${project.id}`;
                  return (
                    <Link
                      key={project.id}
                      to={`/projects/${project.id}`}
                      className="block"
                    >
                      <motion.div
                        whileHover={{ scale: 1.02, x: 4 }}
                        whileTap={{ scale: 0.98 }}
                        className={cn(
                          'flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors relative',
                          isActive
                            ? 'text-accent'
                            : 'text-text-secondary hover:text-text hover:bg-bg-tertiary'
                        )}
                      >
                        {isActive && (
                          <motion.div
                            layoutId="activeSidebar"
                            className="absolute inset-0 bg-accent/10 rounded-xl"
                            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
                          />
                        )}

                        {/* Project icon */}
                        {project.iconUrl ? (
                          <img
                            src={project.iconUrl}
                            alt=""
                            className="w-8 h-8 rounded-lg object-cover relative z-10"
                          />
                        ) : (
                          <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center relative z-10">
                            <span className="text-sm font-bold text-accent">{project.name.charAt(0)}</span>
                          </div>
                        )}

                        <span className="font-medium relative z-10 truncate flex-1 text-left">
                          {project.name}
                        </span>

                        {/* Pending count badge */}
                        {project.pendingApprovalCount > 0 && (
                          <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-amber-500/10 text-amber-500 relative z-10">
                            {project.pendingApprovalCount}
                          </span>
                        )}
                      </motion.div>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        </nav>

        {/* Bottom section - User controls */}
        <div className="p-3 border-t border-border space-y-2">
          {/* Notifications and Theme Toggle row */}
          <div className="flex items-center justify-between px-2">
            {/* Notifications */}
            <div className="relative" ref={notificationsRef}>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowNotifications(!showNotifications)}
                className="p-2.5 rounded-xl hover:bg-bg-tertiary transition-colors relative"
              >
                <Bell className="w-5 h-5" />
                <motion.span
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full"
                />
              </motion.button>

              <AnimatePresence>
                {showNotifications && (
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    transition={{ duration: 0.2 }}
                    className="absolute left-0 bottom-full mb-2 w-72 bg-bg-secondary rounded-2xl border border-border shadow-xl overflow-hidden"
                  >
                    <div className="p-4 border-b border-border">
                      <h3 className="font-semibold">Notifications</h3>
                    </div>
                    <div className="p-4 space-y-3">
                      <div className="flex items-start gap-3 p-2 rounded-lg hover:bg-bg-tertiary transition-colors">
                        <div className="w-8 h-8 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                          <Sparkles className="w-4 h-4 text-accent" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium">New work awaiting approval</p>
                          <p className="text-xs text-text-muted">2 min ago</p>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Theme toggle */}
            <ThemeToggle />
          </div>

          {/* User menu */}
          <div className="relative" ref={userMenuRef}>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="w-full flex items-center gap-3 p-2 rounded-xl hover:bg-bg-tertiary transition-colors"
            >
              {/* Avatar */}
              <div className="relative">
                {user?.avatarUrl ? (
                  <img
                    src={user.avatarUrl}
                    alt={user.name}
                    className="w-9 h-9 rounded-full object-cover ring-2 ring-accent/20"
                  />
                ) : (
                  <motion.div
                    className="w-9 h-9 rounded-full bg-gradient-to-br from-accent to-accent-light flex items-center justify-center text-white font-semibold shadow-lg shadow-accent/20"
                  >
                    {user?.name?.charAt(0) || 'U'}
                  </motion.div>
                )}
                <motion.div
                  className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-bg-secondary"
                  animate={{ scale: [1, 1.2, 1] }}
                  transition={{ duration: 2, repeat: Infinity }}
                />
              </div>
              <div className="flex-1 text-left min-w-0">
                <p className="text-sm font-medium leading-tight truncate">{user?.name}</p>
                <p className="text-xs text-text-muted leading-tight truncate">{user?.email}</p>
              </div>
              <ChevronDown
                className={`w-4 h-4 text-text-muted transition-transform flex-shrink-0 ${showUserMenu ? 'rotate-180' : ''}`}
              />
            </motion.button>

            <AnimatePresence>
              {showUserMenu && (
                <motion.div
                  initial={{ opacity: 0, y: 10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: 10, scale: 0.95 }}
                  transition={{ duration: 0.2 }}
                  className="absolute left-0 right-0 bottom-full mb-2 bg-bg-secondary rounded-2xl border border-border shadow-xl overflow-hidden"
                >
                  {/* User info header */}
                  <div className="p-4 bg-gradient-to-br from-accent/10 to-purple-500/10 border-b border-border">
                    <div className="flex items-center gap-3">
                      {user?.avatarUrl ? (
                        <img
                          src={user.avatarUrl}
                          alt={user.name}
                          className="w-12 h-12 rounded-full object-cover ring-2 ring-white/20"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-accent to-accent-light flex items-center justify-center text-white text-lg font-semibold shadow-lg">
                          {user?.name?.charAt(0) || 'U'}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold truncate">{user?.name}</p>
                        <p className="text-sm text-text-muted truncate">{user?.email}</p>
                      </div>
                    </div>
                  </div>

                  {/* Menu items */}
                  <div className="p-2">
                    <motion.button
                      whileHover={{ x: 4 }}
                      onClick={() => openSettings('profile')}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-bg-tertiary transition-colors text-left"
                    >
                      <div className="w-8 h-8 rounded-lg bg-accent/10 flex items-center justify-center">
                        <Camera className="w-4 h-4 text-accent" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">Profile & Avatar</p>
                        <p className="text-xs text-text-muted">Update your photo</p>
                      </div>
                    </motion.button>

                    <motion.button
                      whileHover={{ x: 4 }}
                      onClick={() => openSettings('password')}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-bg-tertiary transition-colors text-left"
                    >
                      <div className="w-8 h-8 rounded-lg bg-purple-500/10 flex items-center justify-center">
                        <Key className="w-4 h-4 text-purple-500" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">Password</p>
                        <p className="text-xs text-text-muted">Set or change password</p>
                      </div>
                    </motion.button>

                    <div className="my-2 border-t border-border" />

                    <motion.button
                      whileHover={{ x: 4 }}
                      onClick={() => {
                        setShowUserMenu(false);
                        logout();
                      }}
                      className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-red-500/10 text-red-500 transition-colors text-left"
                    >
                      <div className="w-8 h-8 rounded-lg bg-red-500/10 flex items-center justify-center">
                        <LogOut className="w-4 h-4" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">Sign out</p>
                        <p className="text-xs text-red-400">End your session</p>
                      </div>
                    </motion.button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </motion.aside>

      {/* Settings Modal */}
      <UserSettingsModal
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
        initialTab={settingsTab}
      />
    </>
  );
}
